// Cloud document API client.

import { getAuth } from 'firebase/auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

async function authHeaders() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function parseError(res) {
  const data = await res.json().catch(() => ({}));
  const err = new Error(data.message || `Request failed: ${res.status}`);
  err.status = res.status;
  err.code = data.code;
  err.body = data;
  return err;
}

export async function listCloudDocuments({ limit = 50, offset = 0 } = {}) {
  const headers = await authHeaders();
  const url = new URL(`${API}/api/cloud/documents`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));
  const res = await fetch(url, { headers });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function uploadCloudDocument(file, { filename, pageCount, thumbnail } = {}) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  if (filename) form.append('filename', filename);
  if (pageCount) form.append('pageCount', String(pageCount));
  if (thumbnail) form.append('thumbnail', thumbnail, 'thumb.jpg');
  const res = await fetch(`${API}/api/cloud/documents`, { method: 'POST', headers, body: form });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/**
 * Upload (or replace) just the thumbnail for an existing cloud doc. Used
 * by the opportunistic backfill path: when a user opens a doc that has
 * no thumbnail yet, we render one client-side after fetching the bytes
 * and fire-and-forget this.
 */
export async function updateCloudThumbnail(docId, thumbnail) {
  if (!thumbnail) return null;
  const headers = await authHeaders();
  const form = new FormData();
  form.append('thumbnail', thumbnail, 'thumb.jpg');
  const res = await fetch(`${API}/api/cloud/documents/${encodeURIComponent(docId)}/thumbnail`, {
    method: 'PUT',
    headers,
    body: form,
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/**
 * Resolve an authed URL for a cloud doc's thumbnail. Returns a blob URL
 * the caller must revoke when done. Returns null if the doc has no
 * thumbnail stored.
 */
export async function fetchCloudThumbnailBlobUrl(docId) {
  const headers = await authHeaders();
  const res = await fetch(`${API}/api/cloud/documents/${encodeURIComponent(docId)}/thumbnail`, { headers });
  if (res.status === 404) return null;
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Replace an existing cloud document's bytes in place. Preserves the docId —
 * the Viewer session stays cloud-sourced and subsequent ⌘S calls keep using
 * the same endpoint. Server does quota math that subtracts the existing
 * doc's size before checking fit, so a same-size save never fails quota.
 */
export async function updateCloudDocument(docId, file, { filename, pageCount } = {}) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  if (filename) form.append('filename', filename);
  if (pageCount) form.append('pageCount', String(pageCount));
  const res = await fetch(`${API}/api/cloud/documents/${encodeURIComponent(docId)}`, {
    method: 'PUT',
    headers,
    body: form,
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

export async function deleteCloudDocument(docId) {
  const headers = await authHeaders();
  const res = await fetch(`${API}/api/cloud/documents/${encodeURIComponent(docId)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw await parseError(res);
  return res.json();
}

/**
 * Fetch a cloud document's bytes as a File. The API proxies the bytes
 * directly (see routes/cloud.js) so we do one authed request and read
 * the response as a blob — no cross-origin GCS fetch.
 */
export async function fetchCloudDocumentAsFile(docId) {
  const headers = await authHeaders();
  const res = await fetch(`${API}/api/cloud/documents/${encodeURIComponent(docId)}/download`, { headers });
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  const encoded = res.headers.get('X-Scrivar-Filename');
  let filename = 'Document.pdf';
  if (encoded) {
    try { filename = decodeURIComponent(encoded); } catch { /* fall back */ }
  }
  return new File([blob], filename, { type: 'application/pdf' });
}

export async function getCloudUsage() {
  const headers = await authHeaders();
  const res = await fetch(`${API}/api/cloud/usage`, { headers });
  if (!res.ok) throw await parseError(res);
  return res.json();
}
